import { z } from 'zod';
/**
 * 디자인 테마 레지스트리. 관리자가 /admin/branding 에서 선택하면 모든 사용자에게 적용된다.
 * 새 테마를 추가하려면:
 *   1) 여기 배열에 entry 추가 (id, label, hint, swatch)
 *   2) packages/web/src/styles/index.css 에 `:root.theme-<id>.light` / `:root.theme-<id>.dark`
 *      두 블록 추가 (모든 토큰 필수: --background ~ --warning, --shadow-card)
 *   3) 빌드 / 재배포 — 관리자 UI 에 자동 노출.
 *
 * id 는 영구 식별자 (DB 에 저장됨) — 변경 금지. swatch 는 라이트 모드 미리보기 5색.
 */
export const DESIGN_THEMES = [
    {
        id: 'default',
        label: '기본 (따뜻한 크림)',
        hint: 'innermate 출시 디자인. 따뜻한 크림 배경 + 오렌지 액센트.',
        swatch: ['#f3eee5', '#ffffff', '#1f1c1a', '#e35d2b', '#d4ccc0'],
    },
    {
        id: 'aqua',
        label: '청록 미니멀',
        hint: '시원한 청록 액센트. 모던 미니멀 톤.',
        swatch: ['#f0f5f5', '#ffffff', '#1a2426', '#30a8a8', '#d4e0e0'],
    },
    {
        id: 'slate',
        label: '슬레이트 (차분한 회청)',
        hint: '뉴트럴 회청 배경 + 인디고 액센트. 분석·대시보드 용에 적합.',
        swatch: ['#eff2f6', '#ffffff', '#161a22', '#3258d6', '#d4d8de'],
    },
    {
        id: 'forest',
        label: '포레스트 (차분한 녹)',
        hint: '에버그린 톤. 친환경·운영 보고에 잘 맞는 차분한 녹색.',
        swatch: ['#eef3ee', '#ffffff', '#1a2419', '#1f8a52', '#d3dcd3'],
    },
    {
        id: 'violet',
        label: '바이올렛 (모던 보라)',
        hint: '보라 액센트. 디자인/창작 분위기에 어울리는 톤.',
        swatch: ['#f3eef5', '#ffffff', '#22192a', '#7c4dde', '#dcd4e0'],
    },
];
const DESIGN_THEME_IDS = DESIGN_THEMES.map((t) => t.id);
export const DesignThemeIdSchema = z.enum(DESIGN_THEME_IDS);
export const DEFAULT_DESIGN_THEME = 'default';
/**
 * 모르는 id (이전 버전에서 저장된 값 등) 가 들어오면 default 로 폴백.
 */
export function coerceDesignThemeId(raw) {
    if (!raw)
        return DEFAULT_DESIGN_THEME;
    return DESIGN_THEME_IDS.includes(raw)
        ? raw
        : DEFAULT_DESIGN_THEME;
}
/**
 * 인증 없이 호출 가능한 공개 브랜딩 응답.
 * `GET /api/branding` — 사용자 화면(채팅·로그인·온보딩) 좌측 상단에 표시.
 * 서버에서 폴백 적용된 최종 값. 클라이언트는 그대로 사용하면 됨.
 * 관리자 화면(/admin)·위키 사이드바('위키') 는 이 값을 사용하지 않는다 (운영자 식별 위해 고정).
 */
export const PublicBrandingSchema = z.object({
    /** 좌측 상단 텍스트 (폴백: 'innermate'). */
    serviceName: z.string().min(1),
    /** 로고 이미지 URL — 커스텀 있으면 '/api/branding/logo?v=<unix_ts>', 없으면 '/logo.png'. */
    logoUrl: z.string(),
    /** 파비콘 URL — 커스텀 있으면 '/api/branding/favicon?v=<unix_ts>', 없으면 '/favicon.png'. */
    faviconUrl: z.string(),
    /** 전역 디자인 테마 id (레지스트리 참고). 프런트가 <html> 클래스에 theme-<id> 부착. */
    designTheme: DesignThemeIdSchema,
    /**
     * 채팅 입력창 아래 가운데 표시할 안내 문구. 서버가 (enabled && 비어있지 않음) 게이트를 적용한
     * 최종값 — **빈 문자열이면 표시 안 함**. (on/off 플래그는 공개 응답에 포함하지 않는다.)
     */
    chatFooterText: z.string(),
});
/**
 * 어드민 화면에서 사용하는 raw 설정값. null = 미설정 (프런트에서 기본값 폴백).
 */
export const AdminBrandingSchema = z.object({
    /** raw 값. null = 미설정 (사용자 화면에 'innermate' 표시). */
    serviceName: z.string().nullable(),
    hasCustomLogo: z.boolean(),
    logoContentType: z.string().nullable(),
    logoUpdatedAt: z.string().datetime().nullable(),
    hasCustomFavicon: z.boolean(),
    faviconContentType: z.string().nullable(),
    faviconUpdatedAt: z.string().datetime().nullable(),
    /** 현재 선택된 디자인 테마 id. 폴백 = 'default'. */
    designTheme: DesignThemeIdSchema,
    /** 채팅창 하단 안내 문구 raw 값. null = 미설정. */
    chatFooterText: z.string().nullable(),
    /** 안내 문구 표시 on/off. 문구가 있어도 off 면 사용자 화면에 안 보임. */
    chatFooterEnabled: z.boolean(),
});
/**
 * `POST /api/admin/branding/update` body.
 * 필드 미포함 = 변경 없음. null 명시 = 리셋(기본값으로 복귀).
 * designTheme 는 null 불가 — 명시 변경만 허용 (기본값으로 되돌리려면 'default' 전송).
 */
export const UpdateBrandingRequestSchema = z.object({
    serviceName: z.string().trim().min(1).max(100).nullable().optional(),
    designTheme: DesignThemeIdSchema.optional(),
    /** 채팅창 하단 안내 문구. null/빈값 = 리셋(미설정). 미포함 = 변경 없음. */
    chatFooterText: z.string().trim().max(300).nullable().optional(),
    /** 안내 문구 표시 on/off. 미포함 = 변경 없음. */
    chatFooterEnabled: z.boolean().optional(),
});
/** 로고 업로드 시 허용 MIME 화이트리스트. */
export const BRANDING_LOGO_ALLOWED_MIMES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
];
/** 로고 파일 크기 상한 (2MB). */
export const BRANDING_LOGO_MAX_BYTES = 2 * 1024 * 1024;
/**
 * 파비콘 업로드 시 허용 MIME 화이트리스트.
 * ICO 는 브라우저별로 `image/x-icon` (레거시) / `image/vnd.microsoft.icon` (표준) 둘 다 보내므로 모두 수용.
 */
export const BRANDING_FAVICON_ALLOWED_MIMES = [
    'image/png',
    'image/x-icon',
    'image/vnd.microsoft.icon',
    'image/svg+xml',
];
/** 파비콘 파일 크기 상한 — 로고와 동일 (2MB). */
export const BRANDING_FAVICON_MAX_BYTES = BRANDING_LOGO_MAX_BYTES;
//# sourceMappingURL=branding.js.map